import { ReactComponent as CheckIcon } from 'src/assets/svg/checkmark.svg'
import { ReactComponent as DownloadIcon } from 'src/assets/svg/download.svg'
import { ReactComponent as HeartIcon } from 'src/assets/svg/rating/empty-heart.svg'
import { ReactComponent as StarIcon } from 'src/assets/svg/rating/empty-star.svg'
import { ReactComponent as CalendarIcon } from 'src/assets/svg/calendar.svg'
import { ReactComponent as FiltersIcon } from 'src/assets/svg/filters.svg'
import { useIsMobile } from 'src/hooks/useIsMobile'
import { Card } from '../Card/Card'
import { OnboardingStepCta } from './OnboardingStepCta'
import classNames from 'classnames'

import './OnboardingStep.scss'

export enum OnboardingStepStatus {
  COMPLETED = 'completed',
  PENDING = 'pending',
  DISABLED = 'disabled'
}

export interface OnboardingStepProps {
  title: string
  description: string
  status: OnboardingStepStatus
  icon: 'download' | 'heart' | 'star' | 'check' | 'calendar' | 'filters'
  action?: {
    label: string
    onClick?: () => void
    icon?: boolean
  }
  className?: string
  tag?: string
  children?: React.ReactNode
}

const StepIcon = ({ icon }: { icon: OnboardingStepProps['icon'] }) => {
  switch (icon) {
    case 'download':
      return <DownloadIcon />
    case 'heart':
      return <HeartIcon />
    case 'star':
      return <StarIcon />
    case 'calendar':
      return <CalendarIcon />
    case 'filters':
      return <FiltersIcon />
    default:
      return <CheckIcon />
  }
}

export function OnboardingStep({
  title,
  description,
  status,
  icon,
  action,
  tag,
  className = '',
  children
}: Readonly<OnboardingStepProps>) {
  const isMobile = useIsMobile()
  const isEnabled = status !== OnboardingStepStatus.DISABLED
  const isCompleted = status === OnboardingStepStatus.COMPLETED
  const showAction = Boolean(isEnabled && action)
  const stepIcon = isCompleted ? 'check' : icon

  const handleCardClick = () => {
    if (isMobile && showAction && action?.onClick) {
      action.onClick()
    }
  }

  const cardContent = (
    <Card
      className={classNames('onboarding-step', `-${status}`, className, {
        '-clickable': isMobile && showAction
      })}
      data-testid="onboarding-step">
      <div className="onboarding-step__icon">
        <StepIcon icon={stepIcon} />
      </div>
      <div className="onboarding-step__content">
        <div className="onboarding-step__title-container">
          <h3 className="onboarding-step__title">{title}</h3>
          {tag && <span className="onboarding-step__tag">{tag}</span>}
        </div>
        <p className="onboarding-step__description">{description}</p>
      </div>
      {showAction && <OnboardingStepCta action={action} />}
      {children && <div className="onboarding-step__children">{children}</div>}
    </Card>
  )

  if (isMobile && showAction) {
    return (
      <button
        type="button"
        onClick={handleCardClick}
        className="onboarding-step__button">
        {cardContent}
      </button>
    )
  }

  return cardContent
}
